import { useRef, useState } from 'react'
import { Input } from '@/components/ui/input'
import { TimeInput } from '@/components/ui/time-input'
import { TickerSearch } from '@/components/ticker/ticker-search'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { SingleDateInput } from '@/features/sliced-form/components/single-date-input'
import { BacktestNumberInput } from '@/features/backtest/components/backtest-number-input'
import type {
  ParameterDefinition,
  SelectParameterDefinition,
} from '@/features/backtest/schemas/universal-node-schema'
import { cn } from '@/lib/utils'

type ParamValue = string | number | boolean

/** A node row's field: 24px, the standard control width. */
const NODE_CONTROL_CLASS = 'h-[var(--node-row-height)] text-xs w-28 shrink-0 px-2'
/** A subnet card row's field: 20px, the card column's width. */
const CARD_CONTROL_CLASS = 'nodrag h-5 w-full text-xs'

/**
 * Exposed select that doubles as a drag handle. Radix opens the menu on
 * pointer-DOWN, which would hijack a press-and-drag meant to move the node. So
 * the trigger suppresses that down-open (preventDefault makes Radix's composed
 * handler skip) and instead opens on a near-stationary pointer-UP: a real click.
 * When the press turns into a drag, React Flow moves the node (the trigger isn't
 * `nodrag`) and the menu never opens. Mirrors React Flow's own 5px drag threshold.
 */
export function ExposedSelect({
  value,
  options,
  onCommit,
  className,
}: {
  value: string
  options: Array<{ value: string | number | boolean; label: string }>
  onCommit: (v: string | number | boolean) => void
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const press = useRef<{ x: number; y: number; wasOpen: boolean } | null>(null)

  return (
    <Select
      open={open}
      onOpenChange={setOpen}
      value={value}
      onValueChange={(v) => {
        const option = options.find((o) => String(o.value) === v)
        onCommit(option ? option.value : v)
      }}
    >
      <SelectTrigger
        className={className ?? 'h-5 w-full text-xs'}
        onPointerDown={(e) => {
          press.current = { x: e.clientX, y: e.clientY, wasOpen: open }
          e.preventDefault()
        }}
        onPointerUp={(e) => {
          const start = press.current
          press.current = null
          if (start && Math.hypot(e.clientX - start.x, e.clientY - start.y) < 5) {
            setOpen(!start.wasOpen)
          }
        }}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={String(o.value)} value={String(o.value)}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

interface ParamControlProps {
  def: ParameterDefinition
  /** The stored value; unset falls back to the schema default. */
  value: ParamValue | undefined
  onChange: (value: ParamValue) => void
  /**
   * `node`: a node row. Text commits on every keystroke; time and date get
   * their pickers.
   * `card`: the collapsed-subnet card and the Subnet Inspector, whose rows are
   * tighter. The select doubles as a drag handle, text commits on blur or
   * Enter (one undo step per edit), and time and date are typed.
   */
  variant?: 'node' | 'card'
  /** Preset-locked (a strategy-owned feed's symbol and date). */
  disabled?: boolean
  /** Select options when the host narrows the schema's list (Price Data's chart link). */
  options?: SelectParameterDefinition['options']
  /** Sizing, when the host's column sets it. */
  className?: string
  /** The field's accessible name, when its visible name sits apart from it (a column's). */
  ariaLabel?: string
}

/**
 * The field for one node parameter, by its type: the single place a parameter
 * type becomes a control, for the node face, the collapsed-subnet card and the
 * Subnet Inspector alike. Hosts own the label, the row and what a change does.
 */
export function ParamControl({
  def,
  value,
  onChange,
  variant = 'node',
  disabled = false,
  options,
  className,
  ariaLabel,
}: ParamControlProps) {
  const card = variant === 'card'
  const size = className ?? (card ? CARD_CONTROL_CLASS : NODE_CONTROL_CLASS)
  const current = value ?? def.default

  // The card's typed field: commits on blur or Enter, so one edit is one undo step.
  const typedOnBlur = (
    <Input
      key={String(current)}
      defaultValue={String(current)}
      onBlur={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
      }}
      className={size}
    />
  )

  switch (def.type) {
    case 'select': {
      const choices = options ?? def.options
      if (card) {
        return (
          <ExposedSelect
            value={String(current)}
            options={choices}
            onCommit={onChange}
            className={size}
          />
        )
      }
      const locked = disabled || choices.length <= 1
      // Two or three short options as a toggle, every one showing (Chris,
      // 2026-10-06: the Condition's Node vs Static).
      if (def.display === 'segmented') {
        return (
          <div
            role="radiogroup"
            aria-label={ariaLabel ?? def.displayName}
            className={cn('param-segmented nodrag nopan', size)}
          >
            {choices.map((option) => {
              const Icon = 'icon' in option ? option.icon : undefined
              const checked = String(current) === String(option.value)
              return (
                <button
                  key={String(option.value)}
                  type="button"
                  role="radio"
                  aria-checked={checked}
                  disabled={locked}
                  className="param-segmented__option"
                  onClick={(event) => {
                    event.stopPropagation()
                    if (!checked) onChange(option.value)
                  }}
                >
                  {Icon && <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />}
                  {option.label}
                </button>
              )
            })}
          </div>
        )
      }
      // An option's description is its tooltip, in the menu and on the closed field.
      const picked = choices.find((option) => String(option.value) === String(current))
      return (
        <Select
          key={`${def.name}-${current}`}
          value={current?.toString() || def.default?.toString() || ''}
          onValueChange={onChange}
          disabled={locked}
        >
          <SelectTrigger
            className={size}
            disabled={locked}
            aria-label={ariaLabel}
            title={picked?.description}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {choices.map((option) => {
              const Icon = 'icon' in option ? option.icon : undefined
              return (
                <SelectItem
                  key={String(option.value)}
                  value={option.value.toString()}
                  title={option.description}
                >
                  {Icon ? (
                    <span className="flex items-center gap-1.5">
                      <Icon className="h-3 w-3 shrink-0 opacity-80" aria-hidden="true" />
                      {option.label}
                    </span>
                  ) : (
                    option.label
                  )}
                </SelectItem>
              )
            })}
          </SelectContent>
        </Select>
      )
    }

    case 'int':
    case 'float':
      return (
        <BacktestNumberInput
          value={typeof current === 'boolean' ? null : (current ?? null)}
          kind={def.type}
          min={def.min}
          max={def.max}
          step={def.step || (def.type === 'float' ? 0.01 : 1)}
          defaultValue={typeof def.default === 'number' ? def.default : undefined}
          onChange={onChange}
          className={size}
        />
      )

    case 'text':
      if (def.format === 'ticker' && !disabled) {
        // The app's ticker primitive: directory autocomplete, upper-cased and
        // trimmed on commit. `nodrag nopan` keeps React Flow from dragging the
        // node while the user types.
        return (
          <TickerSearch
            value={String(current ?? '')}
            onSelect={(symbol) => onChange(symbol)}
            companionLabel={`${card ? 'subnet' : 'node'}-param-${def.name}`}
            className={
              card
                ? cn(
                    'h-[var(--node-row-height)] rounded border border-input px-1.5 text-foreground',
                    size,
                  )
                : 'nodrag nopan h-[var(--node-row-height)] w-28 shrink-0 rounded border border-input px-1.5 text-foreground'
            }
          />
        )
      }
      if (card) return typedOnBlur
      return (
        <Input
          type="text"
          value={current?.toString() || ''}
          onChange={(e) => onChange(e.target.value)}
          className={cn(size, def.format === 'ticker' && 'uppercase')}
          disabled={disabled}
        />
      )

    case 'time':
      if (card) return typedOnBlur
      return def.native ? (
        <Input
          type="time"
          aria-label={def.displayName}
          value={current?.toString() ?? ''}
          onChange={(event) => onChange(event.target.value)}
          className={size}
        />
      ) : (
        <TimeInput
          value={current?.toString() ?? ''}
          onChange={(next) => onChange(next)}
          presets={def.timePresets}
          size="sm"
          // One pill on the node row (the pre-2026-09-11 look); the split
          // three-trigger layout is for sheets/dialogs.
          variant="compact"
          ariaLabel={def.displayName}
          // A paired row's width (the Trade Manager's From / Until).
          className={className}
        />
      )

    case 'date':
      if (card) return typedOnBlur
      return (
        <SingleDateInput
          value={current?.toString() || ''}
          onChange={(next) => onChange(next)}
          disabled={disabled}
          // px-2 keeps "Dec 31, 2024" whole at the standard control width
          // (px-3 left ~72px and truncated it). The fill/border track the
          // canvas field tokens: this is a Popover button, not an
          // input/combobox, so it escapes the .custom-node-content override in
          // backtest-reactflow.css and would otherwise paint bg-background (a
          // hole in black mode).
          className="px-2 bg-[hsl(var(--bt-input-bg))] border-[hsl(var(--bt-border))]"
        />
      )

    case 'boolean':
      return (
        <Switch
          checked={!!current}
          onCheckedChange={(checked) => onChange(checked === true)}
          className={card ? 'nodrag scale-75' : 'node-toggle'}
          aria-label={ariaLabel}
        />
      )

    default:
      return null
  }
}
